import { z } from 'zod';
import { LAYOUT_RESOLUTION_SOURCES } from '../constants/dashboardLayout.constant';
import { DASHBOARD_SHORTCUT_TARGETS } from '../constants/dashboardShortcut.constant';
import { DASHBOARD_TILE_IDS, TILE_SIZES } from '../constants/dashboardTile.constant';
import { FILL_STATES } from '../constants/fillState.constant';
import { ATTENDANCE_STATES } from '../constants/dutyEvent.constant';
import { ORG_UNIT_TYPES } from '../constants/identity.constant';
import { ARRANGEMENT_STATUSES, calendarDateSchema } from './arrangement.schema';
import { objectIdSchema } from './common.schema';
import { showLifecycleSchema } from './lifecycle.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DASHBOARD'S WIRE FORMAT. [P6a]
 *
 *  ⚠️ THE CATALOGUE IS NOT HERE, AND THE SPLIT IS THE POINT. Which tiles exist, what they
 *  mean and where they drill is `constants/dashboardTile.constant.ts` — zod-free, so P6d can
 *  import it inside a 150 KB budget. This file is only what an untrusted sender produces and
 *  what the server answers with, and it imports the id tuple from there rather than
 *  restating it. The direction never reverses: `officerApp.schema.ts` imports its bounds
 *  from `officerApp.constant.ts` for exactly this reason.
 *
 *  ⚠️ AND THE CATALOGUE IS NOT ON THE WIRE EITHER. Both clients already import
 *  `DASHBOARD_TILES` from the same package; sending it would be a second copy that drifts on
 *  deploy skew, and it would cost the officer app its budget for nothing. The payload names
 *  ids and carries data.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

// ── Bounds ───────────────────────────────────────────────────────────────────────

/**
 * ⚠️ IT THROWS RATHER THAN NARROWING, and a caller cannot raise it — `INBOX_MAX_WINDOW_DAYS`'
 * rule, for `getOrgUnitCounts`' reason. A silently narrowed window under-reports, and a
 * dashboard that under-reports the red count is worse than one that refuses.
 *
 * It agrees with the inbox's 92 by coincidence rather than by derivation: this bound is
 * about how much a fan-out of aggregations may scan, and that argument would hold if the
 * inbox's number moved.
 */
/**
 * ⚠️ [amended C1c §5b] TODAY PLUS THREE — AND THE INBOX'S SEVEN IS DELIBERATELY DIFFERENT.
 *
 * An operator works the inbox through a WEEK of nights; they read the dashboard to decide
 * whether to ring somebody in the next day or two. `resolveRange` in `dashboard.service.ts`
 * carries the full argument, and says there in terms that the next pass must not unify them.
 */
export const DASHBOARD_DEFAULT_WINDOW_DAYS = 3;

export const DASHBOARD_MAX_WINDOW_DAYS = 92;

/**
 * ⚠️ THE SPINE'S BOUND — how many orders one dashboard may reason about.
 *
 * Above it the honest answer is a report rather than a dashboard, so it THROWS with one
 * sentence naming the number and the action. A thousand ObjectIds is about 12 KB into an
 * `$in`, which is comfortable; ten thousand is not, and truncating would under-report every
 * tile at once with nothing on screen to say so.
 */
export const DASHBOARD_MAX_ORDERS_IN_WINDOW = 1_000;

/**
 * ⚠️ SO `?tiles=` CANNOT BE USED TO FAN THE WHOLE CATALOGUE AT ONE POOL ON PURPOSE. The
 * parameter exists to NARROW a re-request to one tile after a failure; it is not a way to
 * ask for more work than a default dashboard does.
 */
/* ⚠️ [amended P6d] DERIVED, NOT A LITERAL. It was 14, grew to 18 at [P6b.1] and is FIFTEEN
   since [P6b.2] collapsed the four shortcut ids into one repeatable tile — three numbers in
   two passes, and not one of them had to be edited here. That is the whole argument for a
   derived bound, and the prose saying otherwise was itself the third stale count. The
   bound means "no more than one of everything", and a hand-typed copy of the catalogue's
   size is a number that goes stale silently the next time a tile is added. */
export const DASHBOARD_MAX_TILES_PER_REQUEST = DASHBOARD_TILE_IDS.length;

/** Per tile, enforced server-side with `maxTimeMS` so the database does the cancelling. */
export const DASHBOARD_TILE_BUDGET_MS = 4_000;
/** Everything waits on the spine, so it gets less. */
export const DASHBOARD_SPINE_BUDGET_MS = 2_000;
/**
 * ⚠️ THE OUTER NET'S HEADROOM OVER THE SERVER DEADLINE. `maxTimeMS` cannot help if the
 * request never reaches the server, if the socket stalls, or if the driver is mid-reconnect;
 * this covers the round trip plus driver bookkeeping. It is a SECOND net and never the only
 * one — a `Promise.race` alone settles the promise and leaves the aggregation running.
 */
export const DASHBOARD_TILE_GRACE_MS = 750;

/** Every tile's bounded drill list. Enough to act on, never a page. */
export const DASHBOARD_DRILL_ROWS = 10;

/**
 * ⚠️ [C1e §1a] HOW MANY DUTY ROWS THE TILE CARRIES — DERIVED, not chosen. Twice the drill
 * page's own bound: six are visible in the scroller and the rest are there to scroll to, and
 * "there are more" is stated rather than implied. This tile is the ONE renderer that does not
 * `.slice()` what it is given, because the scroller is what bounds its HEIGHT.
 */
export const ARRANGEMENTS_TODAY_ROWS = DASHBOARD_DRILL_ROWS * 2;

/**
 * ⚠️ [C2b §6a] WHAT `REQUIREMENT_FULFILMENT` COVERS, IN WORDS, ON THE PAYLOAD.
 *
 * Every other tile obeys the dashboard's date range and this one does not, so an unlabelled
 * exception reads as a bug. It is a CONSTANT rather than the registry `hint` because [C1e §3]
 * made the hint operator-overridable — a structural claim living there can be edited away by
 * the very person it is protecting.
 */
export const DASHBOARD_TODAY_TOMORROW_WINDOW_LABEL = 'Today and tomorrow';

/**
 * ⚠️ [TODAY-TILE] A SECOND FIXED WINDOW, WHICH IS WHY THE ONE ABOVE WAS RENAMED.
 *
 * It was `DASHBOARD_FIXED_WINDOW_LABEL` while there was exactly one fixed-window tile. There
 * are two now — `ARRANGEMENTS_TODAY` covers TODAY and `REQUIREMENT_FULFILMENT` covers today
 * and tomorrow — and a constant called "the fixed window label" that means one of two fixed
 * windows is the second-vocabulary trap [LIFECYCLE] names: the forgotten one fails silently,
 * here as a card that says it covers tomorrow while listing only today. Renamed rather than
 * appended to, which is [C3a §2]'s rule for a comment and holds just as well for a name.
 */
export const DASHBOARD_TODAY_WINDOW_LABEL = 'Today';

/**
 * ⚠️ HOW LATE IS LATE, AND IT IS ON THE WIRE BECAUSE THE SCREEN MUST SAY IT.
 *
 * A lateness percentage with an unstated threshold is a number two people read differently.
 * Fifteen minutes is a bandobast's own tolerance — force is told to report before the hour —
 * and the tile renders it beside the figure rather than assuming a reader knows.
 */
export const DASHBOARD_LATE_START_GRACE_MINUTES = 15;

// ── The query ────────────────────────────────────────────────────────────────────

/**
 * ⚠️ `show` MUST BE DECLARED HERE OR IT IS STRIPPED IN SILENCE. [D4.4] shipped exactly that
 * bug: the web rendered the control, put `show` in its filter keys and genuinely sent
 * `?show=archived`, and the validation middleware ate it — no error, no 422, no log line —
 * because unknown keys are stripped and that is the mass-assignment defence working as
 * designed on a key nobody declared. It stayed that way for a release.
 *
 * ⚠️ AND BOTH DATES ARE OPTIONAL BECAUSE "TODAY" IS THE SERVER'S DEFAULT. Writing today into
 * the URL on mount bakes a date into every shared link and opens a link shared at 23:59 on
 * yesterday. The inbox's rule, and the one difference worth writing down: this defaults to
 * TODAY ALONE where the inbox defaults to today+7 — a snapshot versus a worklist.
 */
export const dashboardQuerySchema = z.object({
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
  show: showLifecycleSchema,
  /*
   * ⚠️ A QUERY STRING IS NOT JSON, so `?tiles=X` arrives as a STRING and `?tiles=X&tiles=Y`
   * as an array. Without the preprocess the single-tile form — which is the ONLY form a
   * per-tile Retry ever sends — fails validation with a message about an array, and the
   * retry button reports a 422 for a request that was perfectly well formed.
   * `undefined` passes straight through to `.optional()`.
   */
  tiles: z.preprocess(
    (value) => (typeof value === 'string' ? [value] : value),
    z.array(z.enum(DASHBOARD_TILE_IDS)).max(DASHBOARD_MAX_TILES_PER_REQUEST).optional(),
  ),
});

export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// ── A tile that could not be answered ────────────────────────────────────────────

/**
 * ⚠️ `TOO_MUCH_DATA` IS NOT RETRYABLE, AND THAT IS THE USEFUL DISTINCTION. Retrying an
 * unchanged 92-day range produces the same refusal and teaches an operator that the app is
 * flaky. A timeout might not.
 */
export const DASHBOARD_TILE_FAILURES = ['TIMED_OUT', 'TOO_MUCH_DATA', 'UNAVAILABLE'] as const;
export type DashboardTileFailure = (typeof DASHBOARD_TILE_FAILURES)[number];

/**
 * ⚠️ THE MESSAGE NAMES THE RANGE AND THE ACTION, NEVER AN INTERNAL DETAIL. §10 bans debug
 * codes in a refusal body, and a collection name, a pipeline stage or a Mongo error code is
 * exactly that — it describes the system to whoever is reading it.
 */
export const dashboardTileErrorSchema = z.object({
  reason: z.enum(DASHBOARD_TILE_FAILURES),
  message: z.string(),
  retryable: z.boolean(),
});

export type DashboardTileError = z.infer<typeof dashboardTileErrorSchema>;

// ── Tile payloads ────────────────────────────────────────────────────────────────

const countByStatusSchema = z.record(z.enum(ARRANGEMENT_STATUSES), z.number().int().nonnegative());

/**
 * ⚠️ `windowIsSuperset` IS NOT DECORATION. This is the one tile that counts ORDERS rather
 * than duties, so it inherits the arrangement-level overlap predicate's deliberate superset
 * for a weekday recurrence — an order that runs Mondays through August matches a Tuesday.
 * [D4] accepted that trade ("showing one order too many is a blemish, missing one is an
 * operational failure") on the condition that the blemish is DISCLOSED. This is where.
 */
export const ordersByStatusTileSchema = z.object({
  total: z.number().int().nonnegative(),
  byStatus: countByStatusSchema,
  windowIsSuperset: z.boolean(),
});

/**
 * ⚠️ [amended C5b §3] FIVE BUCKETS, AND `complete` MEANS EVERY SLOT NAMED. It was "marked
 * complete", which merged a filled duty with one a division DECLARED short at nobody named — two
 * facts an operator acts on differently. `closedShort` is the second, decided per share by the
 * one rule (`fillRank.util.ts`) rather than read off the stored `fill_rank`, which never healed on
 * a row nobody recounted (F2).
 */
const shareStateCountsSchema = z.object({
  notNamed: z.number().int().nonnegative(),
  partial: z.number().int().nonnegative(),
  named: z.number().int().nonnegative(),
  complete: z.number().int().nonnegative(),
  closedShort: z.number().int().nonnegative(),
});

/**
 * ⚠️ TWO GRAINS, LABELLED AS TWO.
 *
 * `unnamedSlots` is a SLOT count — empty force slots addressed to somebody in this range,
 * and the number an operator can act on tonight. `shares` is per addressed unit per duty,
 * which is `allocation_fills`' own grain and is NOT the inbox's row: that groups by
 * (duty, division) on read. A dashboard printing both without saying which is which gets
 * reconciled against the inbox and called wrong.
 */
export const linesUnnamedTileSchema = z.object({
  unnamedSlots: z.number().int().nonnegative(),
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
  shares: shareStateCountsSchema,
});

/**
 * ⚠️ `required` COMES FROM THE DUTY'S STAMPED REQUIREMENT AND `allocated` FROM THE SHARES,
 * so this tile is COMMISSIONERATE-only — see `DASHBOARD_TILES.REQUIREMENT_FULFILMENT`'s
 * header for the four reasons, the last of which is a disclosure by subtraction.
 *
 * Three numbers, three different true statements, and they do not collapse
 * (`deployment.service.ts`'s own words): `required` is what the order ASKS FOR, `sourced` is
 * what the divisions were ASKED TO PROVIDE, and `named` is how many people are against it.
 */
/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C2b §6a] ONE ROW PER ARRANGEMENT, OVER A FIXED WINDOW — A REPLACEMENT, NOT A WIDENING.
 *
 *  It was five scalars drawn as one stacked bar. A commissionerate-wide bar says how far
 *  behind the office is and never WHICH order to ring a division about, which is the only
 *  action the number supports.
 *
 *  ⚠️ `windowLabel` IS ON THE PAYLOAD RATHER THAN IN THE REGISTRY `hint`, AND THAT IS THE
 *  POINT. This tile ignores the dashboard's date range — every other one obeys it, so an
 *  unlabelled exception reads as a bug. [C1e §3] made the hint OPERATOR-OVERRIDABLE, so a
 *  structural claim living there can be edited away by the person it is protecting.
 *
 *  ⚠️ `truncated` IS A COUNT, NOT A BOOLEAN. A silently shortened list reads as "that is all
 *  there is", which on this screen is the difference between an order somebody rings about and
 *  one nobody does — `MAX_SUBTREE_NODES`' rule applied to a tile.
 *
 *  ⚠️ AND IT STAYS COMMISSIONERATE-ONLY. [P6a]'s reason (4) is arithmetic and survives the
 *  reshape untouched: a division's own `allocated` beside a commissionerate-wide `required`
 *  publishes the other divisions' share by subtraction. Per-arrangement rows do not change it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const requirementFulfilmentRowSchema = z.object({
  arrangementId: objectIdSchema,
  /** The derived title — `arrangementDisplayTitle`, the same string every other list shows. */
  displayTitle: z.string(),
  /** ⚠️ [C2b §8b] The department's own number where there is one; the reference otherwise. */
  reference: z.string(),
  /**
   * ⚠️ [C3c.2] THE DEPARTMENT'S OWN ORDER NUMBER, SEPARATELY FROM `reference`.
   *
   * `reference` above already carries `pa_number ?? reference`, so a consumer holding only that
   * cannot tell a number written on paper from an id this system minted — and the dashboard row
   * is now a LINK an operator matches against the order in their hand. `null` means the order
   * has no PA number yet, and the row then shows none rather than a minted one dressed as one.
   *
   * ⚠️ IT IS ADDITIVE AND COSTS NO QUERY: both projections already `.select('… pa_number …')`
   * to build `reference`. Nothing that reads `reference` changes.
   */
  paNumber: z.string().nullable(),
  /**
   * ⚠️ [TODAY-TILE] THE EARLIEST DUTY DATE INSIDE THIS TILE'S OWN WINDOW — NOT `pa_date`.
   *
   * [D4]: an arrangement's date is TWO questions and every query must say which. `pa_date` is
   * ADMINISTRATIVE ("the order was dated X") and is routinely backdated; this row is about
   * work happening today or tomorrow, so the operational date is the only one that answers it.
   *
   * ⚠️ IT IS A `$min` OVER THE WINDOW, SO A TWO-DAY ORDER READS AS TODAY. The tile groups by
   * ARRANGEMENT, so one row can span both days and there is no single true date — the earliest
   * is the one that says "this is already running", which is what the row is opened to learn.
   *
   * ⚠️ NULLABLE, AND THE NULL IS REACHABLE RATHER THAN DEFENSIVE. A row survives on
   * `required > 0 || allocated > 0`, so an order with fills in the window but no surviving
   * requirement row — every duty archived — reaches this with no date to report. Required-key-
   * nullable rather than optional, per [P6d]'s test: the card renders a labelled row either
   * way, so `null` answers a question the screen is asking.
   */
  dutyDate: calendarDateSchema.nullable(),
  required: z.number().int().nonnegative(),
  sourced: z.number().int().nonnegative(),
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
  duties: z.number().int().nonnegative(),
});

export const requirementFulfilmentTileSchema = z.object({
  rows: z.array(requirementFulfilmentRowSchema),
  /** How many orders were dropped past the cap. `0` is the normal case and says so. */
  truncated: z.number().int().nonnegative(),
  /** e.g. `Today and tomorrow` — see the header. */
  windowLabel: z.string(),
});

export const dutyRowSchema = z.object({
  deploymentId: objectIdSchema,
  arrangementId: objectIdSchema,
  reference: z.string(),
  title: z.string(),
  dutyDate: calendarDateSchema,
  slotLabel: z.string(),
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
});

/**
 * ⚠️ IT REPORTS AT THE DUTY GRAIN AND NAMES DUTY POINTS ONLY IN ITS BOUNDED LIST — a stated
 * deviation from the brief's "duty points with nothing staffed".
 *
 * `allocation_fills` carries no duty-point id and must not gain one: that is a denormalised
 * copy of a derived fact with a second writer, plus a migration. Reaching the exact grain
 * needs one seek per duty in the window, which is the most expensive shape in the catalogue.
 * "This gate, this night, has nobody" is cheaper AND more actionable — it is the row an
 * operator opens. `total` is counted after the group and after the zero filter, so the
 * headline never claims a grain the list does not show.
 */
export const dutiesUnstaffedTileSchema = z.object({
  total: z.number().int().nonnegative(),
  rows: z.array(dutyRowSchema),
});

/**
 * ⚠️ COUNTED OVER FORCE ASSIGNMENTS, which is FR-5.4.7's exemption structurally rather than
 * by a rule anybody has to remember: a supervisory seat holds no assignment, so it cannot
 * enter this population. `EXEMPT` is therefore absent from these buckets, not zero.
 */
export const attendanceTileSchema = z.object({
  byState: z.record(z.enum(ATTENDANCE_STATES), z.number().int().nonnegative()),
  reached: z.number().int().nonnegative(),
  notReached: z.number().int().nonnegative(),
  absent: z.number().int().nonnegative(),
  onDuty: z.number().int().nonnegative(),
});

const orderCountRowSchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  title: z.string(),
  count: z.number().int().nonnegative(),
});

/**
 * ⚠️ IT COUNTS RETIRED ROWS, NOT SWAP EVENTS — and the field name has to say so. A slot
 * swapped twice contributes two rows, which is correct: two swaps happened. "Swap count"
 * invites the other reading.
 */
export const swapsTileSchema = z.object({
  swapsPerformed: z.number().int().nonnegative(),
  byOrder: z.array(orderCountRowSchema),
});

/**
 * ⚠️ [C1e §1a] WHAT IS ON TODAY — one row per DUTY, not per order.
 *
 * The grain is `deployment_id`, which is the same grain the inbox groups on minus its division
 * term: a commissionerate viewer's chip therefore means "somebody, somewhere on this duty is
 * still to name", which is the correct commissionerate-level reading of the same rule.
 *
 * ⚠️ `fillState` COMES FROM `RANK_EXPRESSION`, THE EXPRESSION THE INBOX ALREADY SORTS ON —
 * exported for this and read back through `fillStateOfRank`. There were three expressions of
 * `fillStateOf` in this repository and there are still three.
 */
export const arrangementsTodayRowSchema = z.object({
  deploymentId: objectIdSchema,
  arrangementId: objectIdSchema,
  reference: z.string(),
  /**
   * ⚠️ [C3c.2] THE DEPARTMENT'S OWN ORDER NUMBER, SEPARATELY FROM `reference`.
   *
   * `reference` above already carries `pa_number ?? reference`, so a consumer holding only that
   * cannot tell a number written on paper from an id this system minted — and the dashboard row
   * is now a LINK an operator matches against the order in their hand. `null` means the order
   * has no PA number yet, and the row then shows none rather than a minted one dressed as one.
   *
   * ⚠️ IT IS ADDITIVE AND COSTS NO QUERY: both projections already `.select('… pa_number …')`
   * to build `reference`. Nothing that reads `reference` changes.
   */
  paNumber: z.string().nullable(),
  displayTitle: z.string(),
  /** Derived through `dutyPointCards`, so four screens cannot disagree about a card. */
  dutyPointOrdinal: z.number().int().min(1),
  dutyPointTitle: z.string(),
  dutyDate: calendarDateSchema,
  /** ⚠️ THE TIME ALONE — the date has its own column, and `slot_label` leads with the date. */
  timeLabel: z.string(),
  /** ⚠️ [C1e §1a] "units named" IS THE ADDRESSEE LIST — who owes the force on this duty. */
  addressees: z.array(orgUnitSummarySchema),
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
  fillState: z.enum(FILL_STATES),
  /**
   * ⚠️ [C5b §3] WHICH OF COMPLETE's TWO CAUSES — `inboxRowSchema`'s reason. Without it this tile
   * read `All Filled` over a duty every division had closed SHORT at nobody named, and so did
   * its drill table.
   */
  closedShort: z.boolean(),
});

export const arrangementsTodayTileSchema = z.object({
  rows: z.array(arrangementsTodayRowSchema),
  /** Counted AFTER the group and after the zero-ask filter, so it matches what is listed. */
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
  /**
   * ⚠️ [TODAY-TILE] `Today` — AND IT IS ON THE PAYLOAD FOR `REQUIREMENT_FULFILMENT`'s REASON.
   *
   * [C2b §6a]: a tile that declares its own window SAYS SO, and the claim lives here rather
   * than in the registry `hint` because [C1e §3] made the hint OPERATOR-OVERRIDABLE — a
   * structural fact parked there can be edited away by the very person it is protecting.
   *
   * This tile obeyed the dashboard's From/To until now, which is why a card headed "Today's
   * PA" listed the next four days. Every other operational tile still obeys the range, so an
   * unlabelled exception reads as a bug.
   */
  windowLabel: z.string(),
});

/**
 * ⚠️ [C1g §1e] THE BELL'S TWIN — ONE ROW PER ORDER WITH WORK LEFT ON IT.
 *
 * ⚠️ IT IS THE SAME PAYLOAD THE BELL RENDERS, MINUS THE PER-OFFICER HALF. `notificationEntry
 * Schema` carries `isNew` and `arrangementVisible`; neither belongs on a tile — novelty is
 * per officer and the bell owns it, and an entry the caller cannot open is dropped rather
 * than flagged. What is shared is the SOURCE (`outstandingByArrangement`), which is what
 * §1e's "one data source, two renderers" actually means.
 *
 * ⚠️ `total` IS COUNTED AFTER THE GROUP AND AFTER THE STATE FILTER, so a card reading 12
 * above eight rows is honest — `arrangementsTodayTileSchema`'s rule, and the inbox's.
 */
export const ordersToFillRowSchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  displayTitle: z.string(),
  paDate: calendarDateSchema,
  /** OPERATIONAL dates ([D4]) — the row is about duty, not about when the order was cut. */
  firstDutyDate: calendarDateSchema,
  lastDutyDate: calendarDateSchema,
  /** ⚠️ DUTIES, not shares. A duty addressed to three units is one duty. */
  dutyCount: z.number().int().nonnegative(),
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
  /** From `RANK_EXPRESSION` through `fillStateOfRank` — never a second derivation. */
  fillState: z.enum(FILL_STATES),
});

export const ordersToFillTileSchema = z.object({
  rows: z.array(ordersToFillRowSchema),
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [ASSOC-TILE] ONE ROW PER ORDER THAT NAMES THE VIEWER'S OWN UNIT AS AN ASSOCIATED PS.
 *
 *  ⚠️ IT CARRIES NO FILL FIGURE, AND THAT IS THE DIFFERENCE FROM `ordersToFillRowSchema`
 *  RATHER THAN AN OMISSION. An association creates no share — [C1b §4]: an associated unit is
 *  chooseable from any division and `writeShares` upserts a row per PLANNED ADDRESSEE, which
 *  an associated unit is not — so "named of allocated" for this unit does not exist, and a
 *  whole-order figure would be somebody else's work reported on this unit's card. `status` is
 *  what a reader of this list actually wants: is the order still a draft, is it issued, is it
 *  over.
 *
 *  ⚠️ THE RAW `ArrangementStatus` MEMBER, as `ORDERS_BY_STATUS` already puts on this wire.
 *  The officer-facing reduction (`officerDutyState.constant.ts`) is for `/app`, and this
 *  payload is read by `web/` alone.
 *
 *  ⚠️ BOTH DUTY DATES ARE NULLABLE ON THE COLUMN AND CANNOT CURRENTLY ARRIVE NULL ON THIS TILE,
 *  AND SAYING SO IS THE POINT.
 *
 *  They are `default: null` on the model and DERIVED by `syncCounts`, so an order with no duty
 *  point has neither. But every row here is `_id`-bounded by the dashboard SPINE, and
 *  `resolveSpine` matches `last_duty_date: {$gte}` / `first_duty_date: {$lte}` with **no null
 *  arm** — Mongo does not match `$gte` against `null`, so an unscheduled order is excluded from
 *  the spine before this tile sees it. [C3c §8a] added exactly that null arm to
 *  `listArrangements` and states the mechanism; the spine deliberately has none.
 *
 *  ⚠️ SO THE NULLABILITY IS A FLOOR, NOT A LIVE CASE, and it is kept for two reasons: it mirrors
 *  the column truthfully, and the day the spine gains a null arm — which [C3c §8a] shows is a
 *  reachable decision — this contract does not have to change. What must NOT happen is a reader
 *  concluding from the renderer's null branch that unscheduled orders appear here. They do not.
 *
 *  A required key with a nullable value because the row renders a labelled cell either way
 *  (`officerIdentitySchema`'s shape).
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const associatedOrderRowSchema = z.object({
  arrangementId: objectIdSchema,
  /** `pa_number ?? reference` — what the row is called when there is nothing better. */
  reference: z.string(),
  /**
   * ⚠️ SEPARATELY FROM `reference`, for `arrangementsTodayRowSchema`'s stated reason: a
   * consumer holding only that cannot tell a number written on paper from a minted id.
   */
  paNumber: z.string().nullable(),
  displayTitle: z.string(),
  /** ADMINISTRATIVE — what the order is dated ([D4]). Never a duty date. */
  paDate: calendarDateSchema,
  /** OPERATIONAL ([D4]) — when duty actually happens, and `null` before any is scheduled. */
  firstDutyDate: calendarDateSchema.nullable(),
  lastDutyDate: calendarDateSchema.nullable(),
  status: z.enum(ARRANGEMENT_STATUSES),
});

export const associatedOrdersTileSchema = z.object({
  rows: z.array(associatedOrderRowSchema),
  /** Counted before the row bound, so "8 of 23" is honest — `ordersToFillTileSchema`'s rule. */
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
});

/**
 * ⚠️ [C1e §1b] ONE ROW PER DIVISION. `arrangements` is a DISTINCT count of orders touching
 * that division in range, not a sum — a division on three duties of one order is on one order.
 */
export const divisionRollupRowSchema = z.object({
  orgUnitId: objectIdSchema,
  orgUnit: orgUnitSummarySchema.nullable(),
  arrangements: z.number().int().nonnegative(),
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
  /**
   * ⚠️ CLAMPED PER SHARE, NEVER AS ONE SUBTRACTION. A global `allocated - named` lets one
   * share's surplus pay for another's deficit and the number reads LOW —
   * `windowFillTotals` records the same trap for the same reason.
   */
  stillToName: z.number().int().nonnegative(),
});

export const divisionRollupTileSchema = z.object({
  rows: z.array(divisionRollupRowSchema),
  truncated: z.boolean(),
});

const orgUnitLoadRowSchema = z.object({
  orgUnit: orgUnitSummarySchema.nullable(),
  orgUnitId: objectIdSchema,
  allocatedSlots: z.number().int().nonnegative(),
  namedSlots: z.number().int().nonnegative(),
});

export const loadByOrgUnitTileSchema = z.object({
  rows: z.array(orgUnitLoadRowSchema),
  truncated: z.boolean(),
});

/**
 * ⚠️ `withinScope` IS ALWAYS TRUE AND IS ON THE WIRE ANYWAY. At a divisional scope this tile
 * systematically UNDER-reports — an officer worked hard by two divisions looks light to
 * each, because neither may see the other's orders and FR-3.6 says so. A fairness metric
 * that does not state its own boundary becomes an unfairness generator, so the label reads
 * "within your scope" and the flag is what the label is built from.
 */
export const repeatDeploymentTileSchema = z.object({
  rows: z.array(
    z.object({
      officerId: objectIdSchema,
      name: z.string(),
      rankShortName: z.string().nullable(),
      orgUnitName: z.string().nullable(),
      duties: z.number().int().nonnegative(),
      distinctDays: z.number().int().nonnegative(),
    }),
  ),
  withinScope: z.boolean(),
  truncated: z.boolean(),
});

/**
 * ⚠️ PUBLISH → THE DIVISION MARKED COMPLETE, never "became fully named", and
 * `sampleSize`/`outstandingShares` ship beside every average because it is
 * survivorship-biased by construction: only completed shares contribute.
 */
export const publishToCompleteTileSchema = z.object({
  rows: z.array(
    z.object({
      orgUnitId: objectIdSchema,
      orgUnit: orgUnitSummarySchema.nullable(),
      medianHours: z.number().nonnegative(),
      sampleSize: z.number().int().nonnegative(),
    }),
  ),
  outstandingShares: z.number().int().nonnegative(),
});

/**
 * ⚠️ IT READS A DEVICE CLOCK. Rows the server flagged implausible at write time are
 * EXCLUDED, and the exclusion is reported rather than swallowed — a lateness number is a
 * disciplinary number, and one computed from a handset that booted at the epoch is simply
 * wrong. [P5c] stores the flag and never reconciles the two clocks; this respects that.
 */
export const lateStartTileSchema = z.object({
  started: z.number().int().nonnegative(),
  late: z.number().int().nonnegative(),
  graceMinutes: z.number().int().nonnegative(),
  excludedImplausibleClocks: z.number().int().nonnegative(),
});

/**
 * ⚠️ [P7A §3] WHAT THE ORDERS IN RANGE REQUIRE, BY VEHICLE TYPE — and nothing else. `sourced` is GONE:
 * beside `required` it read "1 / 2", a vehicle short-fall, and the client's rule is that a vehicle
 * states what it requires and is never shown short anywhere. A van is counted, never named.
 */
export const vehicleFulfilmentTileSchema = z.object({
  rows: z.array(
    z.object({
      vehicleTypeId: objectIdSchema,
      name: z.string(),
      required: z.number().int().nonnegative(),
    }),
  ),
});

export const ordersByPolicyTileSchema = z.object({
  rows: z.array(
    z.object({
      policyId: objectIdSchema,
      name: z.string(),
      orders: z.number().int().nonnegative(),
    }),
  ),
});

/**
 * Coverage is generated duty-days over the days a standing order was live in the window —
 * the one place `arrangements.duty_date` is the right column, because it is null on every
 * order the generator did not raise.
 */
export const standingOrderCoverageTileSchema = z.object({
  activeOrders: z.number().int().nonnegative(),
  expectedDays: z.number().int().nonnegative(),
  generatedDays: z.number().int().nonnegative(),
  rows: z.array(
    z.object({
      standingOrderId: objectIdSchema,
      reference: z.string(),
      title: z.string(),
      expectedDays: z.number().int().nonnegative(),
      generatedDays: z.number().int().nonnegative(),
    }),
  ),
});

/**
 * ⚠️ [P6b.1] A SHORTCUT READS NOTHING, AND ITS PAYLOAD SAYS SO BY CARRYING ONLY WHERE IT
 * POINTS. The target is CONFIGURATION rather than data — it comes off the layout row, not a
 * collection — and it rides here as well as on the layout entry so a renderer has one place
 * to look. `null` cannot normally reach a viewer: the write path refuses to save a shortcut
 * with no target and the resolver drops one that has none anyway. It is nullable because the
 * TYPE must admit the state the configurator is in mid-edit.
 */
export const shortcutTileSchema = z.object({
  target: z.enum(DASHBOARD_SHORTCUT_TARGETS).nullable(),
});

// ── The tile envelope ────────────────────────────────────────────────────────────

/**
 * ⚠️ `error` IS THE FLAG, AND `data: null` ALONE WOULD NOT BE ENOUGH.
 *
 * A null payload is ambiguous with a quiet week, which is what most dashboards look like
 * outside a bandobast — so a screen that cannot tell them apart renders "—" where it meant
 * "we could not work this out". [P5b] made the same call for `rostersIncluded: false` beside
 * an empty roster: one boolean saying WHICH QUESTION the emptiness answers.
 *
 * ⚠️ AND THE UNION IS DISCRIMINATED ON `id`, WHICH THE CLIENT ALREADY SWITCHES ON to choose a
 * renderer. There is no second discriminator to parse — the existing promise that a payload
 * has one shape is kept.
 */
const tileEnvelope = <K extends (typeof DASHBOARD_TILE_IDS)[number], D extends z.ZodTypeAny>(id: K, data: D) =>
  z.object({
    id: z.literal(id),
    /**
     * ⚠️ [P6b.2] WHICH PLACEMENT THIS IS, BECAUSE A TILE ID IS NO LONGER UNIQUE ON A PAGE.
     * A repeatable tile may appear several times with different destinations and different
     * words; without this the client would have no key to render them by and React would have
     * no key to reconcile them by.
     */
    instanceId: z.string(),
    /** ⚠️ [P6b.2] The row group this placement sits in, 1-based and contiguous. */
    row: z.number().int().min(1),
    size: z.enum(TILE_SIZES),
    /**
     * ⚠️ [amended P6b.2] THE OPERATOR'S WORDS RIDE THE PLACEMENT, NOT THE CATALOGUE ENTRY.
     * They were on `layout[]` while a tile id was unique on a page; two placements of one
     * repeatable tile have two different titles, and a per-CATALOGUE key cannot hold both.
     * `layout[]` goes back to its own job — what this dashboard could hold, and why not.
     *
     * The OVERRIDE, never a resolved English title: a resolved string here would be English on
     * a wire the officer app also reads. `null` means "use the registry's own word", and there
     * ⚠️ [amended C1e §3] THERE IS A HINT KEY NOW — see `dashboardLayout.schema.ts`, where the
     * reversal and what still holds are both written down.
     */
    titleOverride: z.string().nullable(),
    /** ⚠️ [C1e §3] The OVERRIDE, never a resolved string — `titleOverride`'s reason. */
    hintOverride: z.string().nullable(),
    drillLabelOverride: z.string().nullable(),
    error: dashboardTileErrorSchema.nullable(),
    data: data.nullable(),
    elapsedMs: z.number().int().nonnegative(),
  });

export const dashboardTileSchema = z.discriminatedUnion('id', [
  tileEnvelope('ORDERS_BY_STATUS', ordersByStatusTileSchema),
  tileEnvelope('LINES_UNNAMED', linesUnnamedTileSchema),
  tileEnvelope('REQUIREMENT_FULFILMENT', requirementFulfilmentTileSchema),
  tileEnvelope('DUTIES_UNSTAFFED', dutiesUnstaffedTileSchema),
  tileEnvelope('ATTENDANCE', attendanceTileSchema),
  tileEnvelope('SWAPS', swapsTileSchema),
  tileEnvelope('ARRANGEMENTS_TODAY', arrangementsTodayTileSchema),
  tileEnvelope('ORDERS_TO_FILL', ordersToFillTileSchema),
  tileEnvelope('ASSOCIATED_ORDERS', associatedOrdersTileSchema),
  tileEnvelope('DIVISION_ROLLUP', divisionRollupTileSchema),
  tileEnvelope('LOAD_BY_DIVISION', loadByOrgUnitTileSchema),
  tileEnvelope('LOAD_BY_UNIT', loadByOrgUnitTileSchema),
  tileEnvelope('REPEAT_DEPLOYMENT', repeatDeploymentTileSchema),
  tileEnvelope('PUBLISH_TO_COMPLETE', publishToCompleteTileSchema),
  tileEnvelope('LATE_START', lateStartTileSchema),
  tileEnvelope('VEHICLE_FULFILMENT', vehicleFulfilmentTileSchema),
  tileEnvelope('ORDERS_BY_POLICY', ordersByPolicyTileSchema),
  tileEnvelope('STANDING_ORDER_COVERAGE', standingOrderCoverageTileSchema),
  tileEnvelope('SHORTCUT', shortcutTileSchema),
]);

// ── The response ─────────────────────────────────────────────────────────────────

/**
 * ⚠️ THE LAYOUT NAMES EVERY CATALOGUE ENTRY, INCLUDING THE ONES THIS CALLER CANNOT HAVE,
 * WITH THE REASON. A tile that is silently absent looks like a bug, and P6b's configurator
 * needs to render an unavailable tile greyed with an explanation rather than not at all.
 * `unavailableReason` is a REQUIRED key with a NULLABLE value — `officerIdentitySchema`'s
 * shape, because `optional` answers a question no screen can act on.
 */
export const dashboardLayoutEntrySchema = z.object({
  id: z.enum(DASHBOARD_TILE_IDS),
  size: z.enum(TILE_SIZES),
  /** How many times this tile is currently placed. `0` means it is off this dashboard. */
  placements: z.number().int().nonnegative(),
  available: z.boolean(),
  unavailableReason: z.string().nullable(),
  /** ⚠️ [P6b.2] Whether this tile may be placed more than once. */
  repeatable: z.boolean(),
});

export const dashboardSchema = z.object({
  /** What the SERVER resolved, echoed back — so a drill link cannot carry a different range. */
  range: z.object({ from: calendarDateSchema, to: calendarDateSchema, defaulted: z.boolean() }),
  scope: z.object({
    orgUnitType: z.enum(ORG_UNIT_TYPES),
    orgUnit: orgUnitSummarySchema.nullable(),
  }),
  layoutSource: z.enum(LAYOUT_RESOLUTION_SOURCES),
  layout: z.array(dashboardLayoutEntrySchema),
  /** The RESOLVED order. A client renders these and never iterates the catalogue itself. */
  tiles: z.array(dashboardTileSchema),
  /** ⚠️ THE SERVER CLOCK, so a screenshot or a printed sheet says when it was true. */
  generatedAt: z.iso.datetime(),
});

// ── The generic drill page ───────────────────────────────────────────────────────

/**
 * ⚠️ ONE TABLE SHAPE FOR EVERY TILE, RATHER THAN A SECOND DISCRIMINATED UNION.
 *
 * Eight tiles have no screen in this app — there is no attendance list, no swap list and no
 * per-officer deployment list — and FR-5.5.5 still requires each to drill through to its
 * rows. A per-tile payload would be eight more schemas, eight more components and eight more
 * routes for a page whose whole job is to show what the number is made of. The columns are
 * DATA, so the page is one component and P6b grows the detail from here.
 *
 * ⚠️ CELLS ARE STRINGS. Formatting a number is a client concern and a `number | string` cell
 * makes every consumer branch; the server formats once, and `align` is what keeps a column
 * of figures readable.
 */
export const DASHBOARD_ROW_ALIGNMENTS = ['left', 'right'] as const;

/**
 * ⚠️ [C5b §3] A COLUMN WHOSE CELLS ARE A WORD FROM A CLOSED SET SAYS WHICH SET.
 *
 * The drill tables printed `ALLOCATION_IN_PROGRESS` and `COMPLETE` verbatim — "the client is not
 * asked to translate" — so the today tile's drill said `COMPLETE` over a duty closed short at
 * nobody named, three words away from the card that said `Closed short`. The server now sends a
 * WORD (`arrangementStatusWordOf`, `fillWordOf`) and names its vocabulary; the web words it
 * through the same functions its cards use. English stays on the client (`labels.ts`'s rule).
 *
 * ⚠️ OPTIONAL, because fifteen of nineteen projections have no such column, and an absent key
 * means "print the cell as sent", which is the honest default.
 */
export const DASHBOARD_TILE_COLUMN_VOCABULARIES = ['ARRANGEMENT_STATUS_WORD', 'FILL_WORD'] as const;
export type DashboardTileColumnVocabulary = (typeof DASHBOARD_TILE_COLUMN_VOCABULARIES)[number];

export const dashboardTileColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  align: z.enum(DASHBOARD_ROW_ALIGNMENTS),
  vocabulary: z.enum(DASHBOARD_TILE_COLUMN_VOCABULARIES).optional(),
});

export const dashboardTileRowSchema = z.object({
  key: z.string(),
  cells: z.record(z.string(), z.string()),
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [TODAY-TILE] PER **CELL**, NOT PER ROW — AND THAT IS FORCED RATHER THAN CHOSEN.
   *
   *  The drill page needed two destinations in ONE row: the duty point opens that duty's
   *  Assign PA screen and the order number opens the PA's record page. A row-level `href` is
   *  structurally unable to say that, so the key is the COLUMN.
   *
   *  ⚠️ IT DOES NOT BREACH `NameCell`'s RULE, AND THE RULE IS WORTH QUOTING BECAUSE IT LOOKS
   *  LIKE IT DOES. DESIGN_LANGUAGE.md: *"when the name reaches a destination, nothing else in
   *  the row may reach the same one."* Two anchors to two DIFFERENT destinations is exactly
   *  what that permits; two routes to one destination is what it forbids. So a tile whose
   *  title and reference both mean "that order" links ONE of them, never both.
   *
   *  ⚠️ `.optional()`, NOT REQUIRED, AND THE COMPILER IS WHY. `projectTileRows` is a `switch`
   *  over the whole tile union ending in `const unreachable: never = tile`, so a required key
   *  would force all nineteen arms to supply one — including the tiles that genuinely have
   *  nowhere to go (`REPEAT_DEPLOYMENT` has no `/officers/:id`, `VEHICLE_FULFILMENT` no
   *  `/vehicle-types/:id`). An absent map means "this table does not link", which is the
   *  honest default and the state every arm but one is in today.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  hrefs: z.record(z.string(), z.string()).optional(),
});

export const dashboardTileRowsSchema = z.object({
  id: z.enum(DASHBOARD_TILE_IDS),
  /**
   * ⚠️ [P6b] SO THE DRILL PAGE CANNOT SHOW THE REGISTRY TITLE UNDER A TILE THE OPERATOR
   * RENAMED. Same shape and same reason as `dashboardLayoutEntrySchema`'s: the override, never
   * a resolved English string.
   *
   * ⚠️ [amended C1e §3] THIS SENTENCE USED TO END "and no key for a hint". There is one, three
   * lines below — the reversal is recorded in `dashboardLayout.schema.ts`, which is where the
   * tri-state and what still holds are both written down.
   */
  titleOverride: z.string().nullable(),
  hintOverride: z.string().nullable(),
  drillLabelOverride: z.string().nullable(),
  range: z.object({ from: calendarDateSchema, to: calendarDateSchema, defaulted: z.boolean() }),
  columns: z.array(dashboardTileColumnSchema),
  items: z.array(dashboardTileRowSchema),
  total: z.number().int().nonnegative(),
  truncated: z.boolean(),
});

export const dashboardTileParamSchema = z.object({ tileId: z.enum(DASHBOARD_TILE_IDS) });
